/**
 * Global API client with session expiration handling
 *
 * Käsittelee automaattisesti:
 * - Session expired -virheet
 * - Uudelleenohjaus loginiin
 * - Ystävällinen virheilmoitus käyttäjälle
 */

import { API_URL } from '../config/api';

let sessionExpiredHandler = null;

/**
 * Set global session expired handler
 * This should be set from AuthContext
 */
export const setSessionExpiredHandler = (handler) => {
  sessionExpiredHandler = handler;
};

/**
 * Check if response indicates session expired
 */
const isSessionExpiredResponse = (data) => {
  if (!data) return false;

  // Check error code
  if (data.error_code === 'session_expired' || data.error === 'not_authenticated') {
    return true;
  }

  // Check message
  const message = (data.message || '').toLowerCase();
  if (message.includes('not authenticated') ||
      message.includes('session') ||
      message.includes('unauthorized') ||
      message.includes('kirjaudu')) {
    return true;
  }

  return false;
};

/**
 * Enhanced fetch with automatic session expiration handling
 *
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export const apiFetch = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      credentials: 'include',
      ...options
    });

    // Check for session expiration (401 or 403)
    if (response.status === 401 || response.status === 403) {
      try {
        const data = await response.clone().json();

        // Check if it's a session expired error
        if (isSessionExpiredResponse(data)) {
          console.warn('🔒 Session expired, logging out...');

          // Call session expired handler if available
          if (sessionExpiredHandler) {
            sessionExpiredHandler(data.message || 'Istuntosi on vanhentunut. Kirjaudu uudelleen.');
          }

          // Return a rejected promise so the caller knows auth failed
          return Promise.reject({
            sessionExpired: true,
            message: data.message || 'Istunto on vanhentunut'
          });
        }
      } catch (jsonError) {
        // Not JSON or couldn't parse - for 401, assume session expired
        if (response.status === 401) {
          console.warn('🔒 Authentication failed (401), assuming session expired');
          if (sessionExpiredHandler) {
            sessionExpiredHandler('Istuntosi on vanhentunut. Kirjaudu uudelleen.');
          }
          return Promise.reject({
            sessionExpired: true,
            message: 'Istunto on vanhentunut'
          });
        }
      }
    }

    return response;
  } catch (error) {
    // Network error or other fetch error
    console.error('API fetch error:', error);
    throw error;
  }
};

/**
 * Convenience method for JSON API calls
 *
 * @param {string} endpoint - API endpoint (will be prefixed with API_URL)
 * @param {object} options - Fetch options
 * @returns {Promise<object>}
 */
export const apiCall = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  const response = await apiFetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: `HTTP ${response.status}: ${response.statusText}`
    }));
    throw error;
  }

  return response.json();
};
